import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { Activity, Search, Download, RefreshCw, Eye, EyeOff, Users, History } from 'lucide-react';
import toast from 'react-hot-toast';
import { saveStyledXlsx } from '../lib/excelStyle';
import SearchableSelect from '../components/ui/SearchableSelect';
import DateInput from '../components/ui/DateInput';
import { fmtDMY } from '../lib/dateFormat';

// ── Constants ──────────────────────────────────────────────────────
const LIST_COLS = 'id,created_at,action,table_name,record_id,staff_id,staff_name,description';
const PAGE_SIZES = [25, 50, 100, 200];
const EXPORT_CAP = 20000;
const FIELD_LIMIT = 12;               // fields shown before "Show all"

const ACTION_CONFIG = {
  INSERT: { label: 'Added',   color: 'var(--green)',  bg: 'rgba(34,197,94,0.1)',  icon: '➕' },
  UPDATE: { label: 'Updated', color: 'var(--accent)', bg: 'var(--accent-light)',  icon: '✏️' },
  DELETE: { label: 'Deleted', color: 'var(--red)',    bg: 'rgba(239,68,68,0.1)',  icon: '🗑️' },
};
const actionCfg = a => ACTION_CONFIG[a] || { label: a || 'Other', color: 'var(--text2)', bg: 'var(--bg3)', icon: '•' };

const TABLE_LABELS = {
  students: 'Student Profile', attendance: 'Attendance', fee_payments: 'Fee Payment',
  certificate_requests: 'Certificate Request', exam_requests: 'Exam Request', hostel_allocations: 'Hostel Allocation',
  placements: 'Placement', placement_offers: 'Job Offer', assignments: 'Assignment',
  language_training: 'Language Training', student_requests: 'Student Request', internships: 'Internship',
  exam_marks: 'Exam Marks', certificate_distributions: 'Cert Distribution', notifications: 'Notification',
  chat_messages: 'Chat Message', hr_staff: 'HR Staff', hr_salary: 'HR Salary',
  hr_attendance: 'HR Attendance', hr_leave: 'HR Leave', placement_companies: 'Placement Company',
  hr_status: 'HR Status', hostel_bills: 'Hostel Bill', accounts_transactions: 'Accounts Transaction',
  job_postings: 'Job Posting', crm_students: 'CRM Student', crm_call_logs: 'CRM Call Log',
  fee_receipts: 'Fee Receipt', hostel_utility_bills: 'Utility Bill', hostels: 'Hostel',
  hr_advance: 'HR Advance', hr_esi_pf: 'ESI/PF', hr_interview: 'HR Interview',
  mock_interviews: 'Mock Interview', civil_projects: 'Civil Project', construction_projects: 'Construction Project',
  class_schedules: 'Class Schedule', schedule_entries: 'Schedule Entry', gst_purchase_bills: 'GST Purchase',
  gst_sale_bills: 'GST Sale', gst_bill_payments: 'GST Payment', faculties: 'Faculty', course_packages: 'Course Package',
  topics: 'Topic', after_placement: 'After Placement', concerns_requests: 'Concern/Request',
  batch_assignments: 'Batch Assignment', repeat_sessions: 'Repeat Session', student_applications: 'Student Application',
  student_batch_history: 'Batch History', student_course_history: 'Course History', manpower_db: 'Manpower DB',
  staff_project_permissions: 'Staff Project Permission', civil_tenders: 'Civil Tender', schedule_holidays: 'Schedule Holiday',
  hostel_bill_splits: 'Bill Split', crm_colleges: 'CRM College', staff: 'Staff',
};

const FIELD_LABELS = {
  full_name:'Full Name', guardian_name:'Guardian Name', guardian_contact:'Guardian Contact',
  contact_number:'Contact', whatsapp_number:'WhatsApp', email:'Email', dob:'DOB',
  college_name:'College', course_name:'Course', batch_name:'Batch', joining_date:'Joining Date',
  training_timing:'Timing', training_mode:'Mode', centre:'Centre', hostel_needed:'Hostel Needed',
  fee_total:'Fee Total', fee_discount:'Fee Discount', academic_qualification:'Qualification',
  basic_salary:'Basic Salary', present_days:'Present Days', absent_days:'Absent Days',
  bill_amount:'Bill Amount', paid_amount:'Paid Amount', paid_status:'Paid Status',
  company_name:'Company Name', topic_name:'Topic', submitted_date:'Submitted Date',
  corrected:'Status', attendance_mark:'Attendance', staff_name:'Staff Name',
  designation:'Designation', monthly_salary:'Monthly Salary', is_active:'Active',
};
const FIELD_ORDER = Object.keys(FIELD_LABELS);

// values of these fields are hidden until "Reveal sensitive" is switched on
const SENSITIVE = /salary|wage|bonus|incentive|ctc|gross|net_pay|deduction|advance|bank|account_?n|ifsc|aadhaa?r|pan_?n|password|passcode|(^|_)pf(_|$)|(^|_)esi(_|$)/i;
const MASK = '••••••';
const SYSTEM_KEYS = new Set(['id','created_at','updated_at','change_log','updated_by','created_by','search_text']);

const prettify = s => String(s || '').replace(/_/g, ' ').replace(/\s+/g, ' ').trim().replace(/\b\w/g, c => c.toUpperCase());
const tableLabel = t => TABLE_LABELS[t] || prettify(t);
const fieldLabel = k => FIELD_LABELS[k] || prettify(k);

// ── Date helpers ───────────────────────────────────────────────────
const pad2 = n => String(n).padStart(2, '0');
const dayKey = d => { const x = new Date(d); return `${x.getFullYear()}-${pad2(x.getMonth()+1)}-${pad2(x.getDate())}`; };
// start/end of a local day as UTC instants (a bare 'YYYY-MM-DDT00:00:00' would be read as UTC, 5:30 hours off)
const dayStartISO = d => new Date(`${d}T00:00:00`).toISOString();
const dayEndISO   = d => new Date(`${d}T23:59:59.999`).toISOString();
const addDays = n => { const d = new Date(); d.setDate(d.getDate() + n); return dayKey(d); };
const timeOf = ts => new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
function timeAgo(ts) {
  const mins = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days < 30 ? `${days}d ago` : '';
}

// ── Change detection ───────────────────────────────────────────────
// readable value (objects/arrays used to print as [object Object] and hide real changes)
const str = v => v == null ? '' : (typeof v === 'object' ? JSON.stringify(v) : String(v));
const norm = v => { const s = str(v).trim(); return s === 'null' ? '' : s; };
const maskVal = (key, val, reveal) => (!reveal && val !== '' && SENSITIVE.test(key)) ? MASK : val;
const orderKeys = keys => {
  const known = keys.filter(k => FIELD_LABELS[k]).sort((a, b) => FIELD_ORDER.indexOf(a) - FIELD_ORDER.indexOf(b));
  const rest  = keys.filter(k => !FIELD_LABELS[k]).sort();
  return [...known, ...rest];
};

// Returns every changed field (no silent cut-off) and whether the old values were recorded.
function getChangedFields(oldData, newData, action) {
  const o = oldData || {}, n = newData || {};
  const keep = k => !SYSTEM_KEYS.has(k);
  if (action === 'INSERT') {
    const rows = orderKeys(Object.keys(n).filter(k => keep(k) && norm(n[k]) !== ''))
      .map(k => ({ key: k, field: fieldLabel(k), old: '', new: str(n[k]) }));
    return { rows, noOld: false };
  }
  if (action === 'DELETE') {
    const rows = orderKeys(Object.keys(o).filter(k => keep(k) && norm(o[k]) !== ''))
      .map(k => ({ key: k, field: fieldLabel(k), old: str(o[k]), new: '' }));
    return { rows, noOld: false };
  }
  if (!oldData && newData) {   // an UPDATE logged without its old values: show what we have
    const rows = orderKeys(Object.keys(n).filter(k => keep(k) && norm(n[k]) !== ''))
      .map(k => ({ key: k, field: fieldLabel(k), old: '', new: str(n[k]) }));
    return { rows, noOld: true };
  }
  const keys = [...new Set([...Object.keys(o), ...Object.keys(n)])].filter(k => keep(k) && norm(o[k]) !== norm(n[k]));
  const rows = orderKeys(keys).map(k => ({ key: k, field: fieldLabel(k), old: str(o[k]), new: str(n[k]) }));
  return { rows, noOld: false };
}
function changeText(l, reveal) {
  const { rows } = getChangedFields(l.old_data, l.new_data, l.action);
  return rows.map(c => {
    const ov = maskVal(c.key, c.old, reveal), nv = maskVal(c.key, c.new, reveal);
    return l.action === 'INSERT' ? `${c.field}: ${nv}` : l.action === 'DELETE' ? `${c.field}: ${ov}` : `${c.field}: ${ov || '—'} → ${nv || '—'}`;
  }).join('; ');
}
const maskObject = (obj, reveal) => {
  if (!obj || reveal) return obj;
  const out = {};
  Object.keys(obj).forEach(k => { out[k] = (SENSITIVE.test(k) && norm(obj[k]) !== '') ? MASK : obj[k]; });
  return out;
};

// ── Query building (all filtering happens in the database) ─────────
const ACTION_WORDS = { INSERT: /^(add|insert|new|creat)/, UPDATE: /^(upd|edit|chang|modif)/, DELETE: /^(del|remov)/ };
function searchClause(term, useText) {
  const t = String(term || '').replace(/[,()%*\\"]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!t) return null;
  const like = `%${t}%`, tl = t.toLowerCase();
  const parts = useText
    ? [`search_text.ilike.${like}`]
    : [`description.ilike.${like}`, `staff_name.ilike.${like}`, `table_name.ilike.${like}`];
  if (tl.length >= 3) {
    const names = Object.keys(TABLE_LABELS).filter(k => TABLE_LABELS[k].toLowerCase().includes(tl)).slice(0, 40);
    if (names.length) parts.push(`table_name.in.(${names.join(',')})`);
    Object.keys(ACTION_WORDS).forEach(a => { if (ACTION_WORDS[a].test(tl)) parts.push(`action.eq.${a}`); });
  }
  return parts.join(',');
}
function applyFilters(q, f, useText, opts = {}) {
  // filter by the recorded name: older entries have no staff_id, but every entry has the name
  if (f.staff) q = q.eq('staff_name', f.staff);
  if (!opts.skipAction && f.action) q = q.eq('action', f.action);
  if (f.record) q = q.eq('table_name', f.record.table).eq('record_id', String(f.record.id));
  else if (f.table) q = q.eq('table_name', f.table);
  if (!opts.skipDate) {
    if (f.from) q = q.gte('created_at', dayStartISO(f.from));
    if (f.to)   q = q.lte('created_at', dayEndISO(f.to));
  }
  const clause = searchClause(f.search, useText);
  if (clause) q = q.or(clause);
  return q;
}
const isSearchTextErr = e => !!e && /search_text/i.test(e.message || '');
const isTableMissingErr = e => !!e && (e.code === '42P01' || /relation .* does not exist|could not find the table/i.test(e.message || ''));

const SETUP_SQL = `-- Run the full activity_log_setup.sql file. Minimum:
CREATE TABLE IF NOT EXISTS activity_logs (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  created_at TIMESTAMPTZ DEFAULT NOW(),
  staff_id UUID REFERENCES staff(id) ON DELETE SET NULL,
  staff_name TEXT,
  action TEXT NOT NULL,          -- INSERT, UPDATE, DELETE
  table_name TEXT NOT NULL,
  record_id TEXT,
  description TEXT,
  old_data JSONB,
  new_data JSONB
);
ALTER TABLE activity_logs ENABLE ROW LEVEL SECURITY;
CREATE POLICY "activity_read"   ON activity_logs FOR SELECT TO authenticated USING (true);
CREATE POLICY "activity_insert" ON activity_logs FOR INSERT TO authenticated WITH CHECK (true);
-- no UPDATE / DELETE policy: the log cannot be edited from the app`;

// ═══════════════════════════════════════════════════════════════════
// Expanded detail (loaded only when a row is opened)
// ═══════════════════════════════════════════════════════════════════
function DetailPanel({ log, detail, reveal, onHistory }) {
  const [showAll, setShowAll] = useState(false);
  const [raw, setRaw] = useState(false);
  const box = { padding: '12px 16px', background: 'var(--bg3)', borderTop: '1px solid var(--border)' };
  if (detail === undefined || detail === 'loading') return <div style={{ ...box, fontSize: 12, color: 'var(--text3)' }}>Loading details…</div>;
  if (detail === 'error') return <div style={{ ...box, fontSize: 12, color: 'var(--red)' }}>Could not load the details of this entry.</div>;
  const { rows, noOld } = getChangedFields(detail.old, detail.new, log.action);
  const shown = showAll ? rows : rows.slice(0, FIELD_LIMIT);
  const cell = { padding: '4px 8px', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  return (
    <div style={box}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8, fontSize: 11, color: 'var(--text3)' }}>
        <span>Record: <span style={{ fontFamily: 'monospace' }}>{log.record_id || '—'}</span></span>
        {log.record_id && <button className="btn btn-ghost btn-sm" onClick={() => onHistory(log)}><History size={12} /> History of this record</button>}
        <button className="btn btn-ghost btn-sm" onClick={() => setRaw(r => !r)}>{raw ? 'Show changes' : 'Show raw JSON'}</button>
      </div>
      {noOld && <div style={{ fontSize: 11, color: 'var(--amber)', marginBottom: 6 }}>Old values were not recorded for this entry — showing the new values only.</div>}
      {raw ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[['Old data', detail.old], ['New data', detail.new]].map(([t, d]) => (
            <div key={t}>
              <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }}>{t}</div>
              <pre style={{ fontSize: 11, margin: 0, padding: 8, background: 'var(--surface)', borderRadius: 6, maxHeight: 260, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{d ? JSON.stringify(maskObject(d, reveal), null, 2) : '—'}</pre>
            </div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 11, color: 'var(--text3)' }}>No field-level changes were recorded (only system fields changed).</div>
      ) : (
        <>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11 }}>
            <thead>
              <tr style={{ background: 'var(--surface)' }}>
                <th style={{ ...cell, textAlign: 'left', color: 'var(--text3)' }}>Field</th>
                {log.action !== 'INSERT' && <th style={{ ...cell, textAlign: 'left', color: 'var(--red)' }}>Old</th>}
                {log.action !== 'DELETE' && <th style={{ ...cell, textAlign: 'left', color: 'var(--green)' }}>New</th>}
              </tr>
            </thead>
            <tbody>
              {shown.map(c => {
                const ov = maskVal(c.key, c.old, reveal), nv = maskVal(c.key, c.new, reveal);
                return (
                  <tr key={c.key} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ ...cell, fontWeight: 600 }}>{c.field}</td>
                    {log.action !== 'INSERT' && <td style={{ ...cell, color: 'var(--red)' }} title={ov}>{ov || '—'}</td>}
                    {log.action !== 'DELETE' && <td style={{ ...cell, color: 'var(--green)' }} title={nv}>{nv || '—'}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length > FIELD_LIMIT && (
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 6 }} onClick={() => setShowAll(v => !v)}>
              {showAll ? 'Show fewer fields' : `Show all ${rows.length} fields (+${rows.length - FIELD_LIMIT} more)`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════════
export default function ActivityLog() {
  const [rows, setRows]           = useState([]);
  const [total, setTotal]         = useState(null);   // approximate, loaded after the list
  const [hasNext, setHasNext]     = useState(false);
  const [stats, setStats]         = useState(null);   // {ins, upd, del, today}
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]       = useState('');
  const [filterStaff, setFilterStaff]   = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterTable, setFilterTable]   = useState('');
  const [filterDate, setFilterDate]     = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [record, setRecord]       = useState(null);   // {table, id} → history of one record
  const [asc, setAsc]             = useState(false);
  const [page, setPage]           = useState(1);
  const [pageSize, setPageSize]   = useState(50);
  const [expandedRow, setExpandedRow] = useState(null);
  const [details, setDetails]     = useState({});
  const [reveal, setReveal]       = useState(false);
  const [newCount, setNewCount]   = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  const [summary, setSummary]     = useState(null);   // null = loading, 'na' = SQL not installed, {error} = failed
  const [summaryAll, setSummaryAll] = useState(false); // false = last 7 days unless dates are chosen
  const [summaryKey, setSummaryKey] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [, setTick]               = useState(0);
  const reqId = useRef(0);
  const searchCols = useRef({ text: false });         // set to true only if you add the optional search_text column (see setup SQL)

  const todayKey = dayKey(new Date());
  const dateInvalid = !!(filterDate && filterDateTo && filterDate > filterDateTo);
  const filters = { search, staff: filterStaff, action: filterAction, table: filterTable, from: filterDate, to: filterDateTo, record };
  const anyFilter = !!(search || searchInput || filterStaff || filterAction || filterTable || filterDate || filterDateTo || record);

  useEffect(() => { loadStaff(); }, []);
  // debounce typing so we don't query on every keystroke
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);
  useEffect(() => { loadData(); }, [search, filterStaff, filterAction, filterTable, filterDate, filterDateTo, record, asc, page, pageSize, refreshKey]);
  // "x minutes ago" stays fresh
  useEffect(() => { const t = setInterval(() => setTick(x => x + 1), 60000); return () => clearInterval(t); }, []);
  // live: tell the user when new entries arrive (does nothing if realtime is not enabled for the table)
  useEffect(() => {
    let ch;
    try {
      ch = supabase.channel('activity-log-live')
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'activity_logs' }, () => setNewCount(c => c + 1))
        .subscribe();
    } catch (e) { /* realtime unavailable */ }
    return () => { if (ch) supabase.removeChannel(ch); };
  }, []);
  // per-staff summary — limited to the last 7 days unless dates are chosen, so it never scans the whole log
  useEffect(() => {
    if (!showSummary) return;
    let alive = true;
    setSummary(null);
    const fromDay = filterDate || (summaryAll ? '' : addDays(-6));
    (async () => {
      const { data, error } = await supabase.rpc('activity_staff_summary', {
        p_from: fromDay ? dayStartISO(fromDay) : null,
        p_to:   filterDateTo ? dayEndISO(filterDateTo) : null,
      });
      if (!alive) return;
      if (!error) { setSummary(data || []); return; }
      const missing = error.code === 'PGRST202' || /could not find the function|function .* does not exist/i.test(error.message || '');
      setSummary(missing ? 'na' : { error: error.message || 'The request failed' });
    })();
    return () => { alive = false; };
  }, [showSummary, filterDate, filterDateTo, summaryAll, summaryKey]);

  async function loadStaff() {
    const { data } = await supabase.from('staff').select('id, full_name').order('full_name').limit(500);
    setStaffList(data || []);
  }

  async function loadData() {
    const my = ++reqId.current;
    setLoading(true);
    if (dateInvalid) { setRows([]); setHasNext(false); setTotal(0); setStats(null); setLoadError(null); setLoading(false); return; }
    const f = filters;
    const from = (page - 1) * pageSize;
    // 1) The list comes first and is shown at once. One extra row tells us whether a next page exists,
    //    so no slow COUNT is needed before anything appears.
    let lr;
    for (let attempt = 0; attempt < 2; attempt++) {
      const useText = searchCols.current.text;
      lr = await applyFilters(supabase.from('activity_logs').select(LIST_COLS), f, useText)
        .order('created_at', { ascending: asc }).order('id', { ascending: asc }).range(from, from + pageSize);
      if (isSearchTextErr(lr.error) && useText) { searchCols.current.text = false; continue; }
      break;
    }
    if (my !== reqId.current) return;   // a newer request already took over
    if (lr.error) {
      setRows([]); setHasNext(false); setTotal(0); setStats(null); setLoadError(lr.error); setLoading(false);
      return;
    }
    const data = lr.data || [];
    setLoadError(null);
    setRows(data.slice(0, pageSize));
    setHasNext(data.length > pageSize);
    setLoading(false);

    // 2) The counts are estimates fetched afterwards, so they can never hold the table back.
    setTotal(null); setStats(null);
    const useText = searchCols.current.text;
    const head = mode => supabase.from('activity_logs').select('id', { count: mode, head: true });
    const cnt = a => applyFilters(head('estimated'), f, useText, { skipAction: true }).eq('action', a);
    const today = applyFilters(head('exact'), f, useText, { skipDate: true }).gte('created_at', dayStartISO(todayKey));
    const [t, i, u, d, td] = await Promise.all([applyFilters(head('estimated'), f, useText), cnt('INSERT'), cnt('UPDATE'), cnt('DELETE'), today]);
    if (my !== reqId.current) return;
    setTotal(t.error ? null : t.count);
    setStats({ ins: i.error ? null : i.count, upd: u.error ? null : u.count, del: d.error ? null : d.count, today: td.error ? null : td.count });
  }

  async function toggleRow(l) {
    const open = expandedRow === l.id;
    setExpandedRow(open ? null : l.id);
    if (open || (details[l.id] !== undefined && details[l.id] !== 'error')) return;
    setDetails(m => ({ ...m, [l.id]: 'loading' }));
    const { data, error } = await supabase.from('activity_logs').select('old_data,new_data').eq('id', l.id).single();
    setDetails(m => ({ ...m, [l.id]: error || !data ? 'error' : { old: data.old_data, new: data.new_data } }));
  }

  function clearAll() {
    setSearchInput(''); setSearch(''); setFilterStaff(''); setFilterAction(''); setFilterTable('');
    setFilterDate(''); setFilterDateTo(''); setRecord(null); setPage(1);
  }
  const setRange = (a, b) => { setFilterDate(a); setFilterDateTo(b); setPage(1); };
  const monthStart = `${todayKey.slice(0, 8)}01`;
  const presets = [
    ['Today', todayKey, todayKey], ['Yesterday', addDays(-1), addDays(-1)],
    ['7 days', addDays(-6), todayKey], ['30 days', addDays(-29), todayKey], ['This month', monthStart, todayKey],
  ];

  function openHistory(l) {
    setRecord({ table: l.table_name, id: l.record_id });
    setFilterTable(''); setPage(1); setExpandedRow(null);
  }

  // Export every row that matches the filters (not just the page on screen)
  async function exportLogs() {
    if (rows.length === 0) { toast.error('Nothing to export'); return; }
    setExporting(true);
    const tid = toast.loading('Preparing export…');
    try {
      let all = [];
      for (let from = 0; from < EXPORT_CAP; from += 1000) {
        const { data, error } = await applyFilters(
          supabase.from('activity_logs').select('id,created_at,action,table_name,record_id,staff_name,description,old_data,new_data'),
          filters, searchCols.current.text)
          .order('created_at', { ascending: asc }).order('id', { ascending: asc }).range(from, from + 999);
        if (error) throw error;
        all = all.concat(data || []);
        if ((data || []).length < 1000) break;
      }
      const out = all.map((l, i) => ({
        'Sl': i + 1,
        'Date': fmtDMY(dayKey(l.created_at)),
        'Time': timeOf(l.created_at),
        'Staff': l.staff_name || '',
        'Action': actionCfg(l.action).label,
        'Section': tableLabel(l.table_name),
        'Record ID': l.record_id || '',
        'Description': l.description || '',
        'Changes': changeText(l, reveal).slice(0, 32000),
      }));
      saveStyledXlsx(`Activity_Log_${todayKey}.xlsx`, [{ name: 'Activity Log', title: 'Activity Log', rows: out,
        widths: { 'Sl': 6, 'Date': 12, 'Time': 9, 'Staff': 20, 'Action': 10, 'Section': 20, 'Record ID': 14, 'Description': 44, 'Changes': 70 } }]);
      toast.success(`Exported ${out.length} entries ✅${out.length >= EXPORT_CAP ? ` (first ${EXPORT_CAP} only — narrow the filters for more)` : ''}${reveal ? '' : ' (sensitive values hidden)'}`, { id: tid });
    } catch (e) {
      toast.error('Export failed: ' + (e.message || e), { id: tid });
    }
    setExporting(false);
  }

  const tables = Object.keys(TABLE_LABELS).sort((a, b) => TABLE_LABELS[a].localeCompare(TABLE_LABELS[b]));
  if (filterTable && !tables.includes(filterTable)) tables.push(filterTable);
  const tableMissing = isTableMissingErr(loadError);
  const firstRow = rows.length ? (page - 1) * pageSize + 1 : 0;
  const lastRow = (page - 1) * pageSize + rows.length;
  const fmtN = v => (v == null ? '—' : Number(v).toLocaleString('en-IN'));
  const th = { padding: '8px 14px', background: 'var(--accent-light)', textAlign: 'left', color: 'var(--accent)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Activity Log</h1>
          <p className="page-subtitle">Track all staff actions — additions, edits and deletions</p>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowSummary(v => !v)}><Users size={13} /> By staff</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setReveal(v => !v)} title="Salary, bank and ID values are hidden by default">
            {reveal ? <EyeOff size={13} /> : <Eye size={13} />} {reveal ? 'Hide sensitive' : 'Reveal sensitive'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => { setNewCount(0); setRefreshKey(k => k + 1); }}><RefreshCw size={13} /> Refresh</button>
          <button className="btn btn-secondary btn-sm" onClick={exportLogs} disabled={exporting}><Download size={13} /> {exporting ? 'Exporting…' : 'Export'}</button>
        </div>
      </div>

      {newCount > 0 && (
        <div style={{ marginBottom: 12, padding: '8px 14px', borderRadius: 8, background: 'var(--accent-light)', color: 'var(--accent)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
          🔔 {newCount} new {newCount === 1 ? 'activity' : 'activities'}
          <button className="btn btn-primary btn-sm" onClick={() => { setNewCount(0); setAsc(false); setPage(1); setRefreshKey(k => k + 1); }}>Show</button>
        </div>
      )}

      {/* Stats — real totals from the database, follow the filters */}
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        {[
          { label: 'Matching entries (approx.)', value: total == null ? '…' : fmtN(total), color: 'var(--text2)' },
          { label: 'Today', value: (stats ? fmtN(stats.today) : '…'), color: 'var(--accent)' },
          { label: 'Added', value: (stats ? fmtN(stats.ins) : '…'), color: 'var(--green)', filter: 'INSERT' },
          { label: 'Updated', value: (stats ? fmtN(stats.upd) : '…'), color: 'var(--amber)', filter: 'UPDATE' },
          { label: 'Deleted', value: (stats ? fmtN(stats.del) : '…'), color: 'var(--red)', filter: 'DELETE' },
        ].map(s => (
          <div key={s.label} className="stat-card"
            style={{ cursor: s.filter ? 'pointer' : undefined, outline: s.filter && filterAction === s.filter ? `2px solid ${s.color}` : undefined }}
            onClick={() => s.filter && (setFilterAction(filterAction === s.filter ? '' : s.filter), setPage(1))}>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: s.color, fontSize: 24 }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ margin: '-12px 0 16px', fontSize: 11, color: 'var(--text3)' }}>Counts are estimates on a large log; the list below is exact.</div>

      {/* Per-staff summary */}
      {showSummary && (
        <div className="card" style={{ padding: 14, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>Activity by staff {filterDate || filterDateTo ? '(selected dates)' : summaryAll ? '(all time)' : '(last 7 days)'}</span>
            {!filterDate && !filterDateTo && (
              <button className="btn btn-ghost btn-sm" onClick={() => setSummaryAll(v => !v)}>{summaryAll ? 'Back to last 7 days' : 'Show all time (slow)'}</button>
            )}
          </div>
          {summary === null ? <div style={{ fontSize: 12, color: 'var(--text3)' }}>Loading…</div>
            : summary === 'na' ? <div style={{ fontSize: 12, color: 'var(--text3)' }}>Run <b>activity_log_setup.sql</b> in Supabase to enable this summary.</div>
            : summary.error ? <div style={{ fontSize: 12, color: 'var(--red)' }}>Could not load the summary: {summary.error}. Try a shorter date range. <button className="btn btn-ghost btn-sm" onClick={() => setSummaryKey(k => k + 1)}>Retry</button></div>
            : summary.length === 0 ? <div style={{ fontSize: 12, color: 'var(--text3)' }}>No activity in this period.</div>
            : (
              <div style={{ overflowX: 'auto', maxHeight: 260 }}>
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead><tr style={{ textAlign: 'left', color: 'var(--text3)' }}>
                    <th style={{ padding: '4px 8px' }}>Staff</th><th style={{ padding: '4px 8px', textAlign: 'right' }}>Added</th>
                    <th style={{ padding: '4px 8px', textAlign: 'right' }}>Updated</th><th style={{ padding: '4px 8px', textAlign: 'right' }}>Deleted</th>
                    <th style={{ padding: '4px 8px', textAlign: 'right' }}>Total</th>
                  </tr></thead>
                  <tbody>
                    {summary.map((s, i) => (
                      <tr key={s.staff_name || ('x' + i)} style={{ borderTop: '1px solid var(--border)', cursor: s.staff_name ? 'pointer' : 'default' }}
                        onClick={() => { if (s.staff_name) { setFilterStaff(s.staff_name); setPage(1); } }} title="Click to filter by this person">
                        <td style={{ padding: '4px 8px', fontWeight: 600 }}>{s.staff_name || '(no name recorded)'}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', color: 'var(--green)' }}>{fmtN(s.added)}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', color: 'var(--amber)' }}>{fmtN(s.updated)}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', color: 'var(--red)' }}>{fmtN(s.deleted)}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 700 }}>{fmtN(s.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="search-bar" style={{ flex: 1, minWidth: 200 }}>
          <Search /><input placeholder="Search staff, section, action, description…" value={searchInput} onChange={e => setSearchInput(e.target.value)} />
        </div>
        <SearchableSelect
          options={[{ value: 'System', label: 'System (automatic)' }, ...[...new Set(staffList.map(s => s.full_name).filter(n => n && n !== 'System'))].map(n => ({ value: n, label: n }))]}
          value={filterStaff}
          onChange={v => { setFilterStaff(v); setPage(1); }}
          placeholder="All Staff"
          searchPlaceholder="Search staff..."
          style={{ minWidth: 180 }}
        />
        <select value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(1); }} style={{ minWidth: 130 }}>
          <option value="">All Actions</option>
          <option value="INSERT">➕ Added</option>
          <option value="UPDATE">✏️ Updated</option>
          <option value="DELETE">🗑️ Deleted</option>
        </select>
        <select value={filterTable} onChange={e => { setFilterTable(e.target.value); setRecord(null); setPage(1); }} style={{ minWidth: 160 }}>
          <option value="">All Sections</option>
          {tables.map(t => <option key={t} value={t}>{tableLabel(t)}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <DateInput value={filterDate} onChange={e => { setFilterDate(e.target.value); setPage(1); }} style={{ width: 140 }} placeholder="From date" />
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>to</span>
          <DateInput value={filterDateTo} onChange={e => { setFilterDateTo(e.target.value); setPage(1); }} style={{ width: 140 }} placeholder="To date" />
        </div>
        {anyFilter && <button className="btn btn-secondary btn-sm" onClick={clearAll}>✕ Clear</button>}
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        {presets.map(([label, a, b]) => {
          const on = filterDate === a && filterDateTo === b;
          return <button key={label} className={`btn btn-sm ${on ? 'btn-primary' : 'btn-ghost'}`} onClick={() => on ? setRange('', '') : setRange(a, b)}>{label}</button>;
        })}
        {dateInvalid && <span style={{ fontSize: 12, color: 'var(--red)' }}>⚠ “From” date is after the “To” date</span>}
        {record && (
          <span style={{ fontSize: 12, background: 'var(--accent-light)', color: 'var(--accent)', padding: '3px 10px', borderRadius: 20 }}>
            History of {tableLabel(record.table)} <span style={{ fontFamily: 'monospace' }}>{String(record.id).slice(0, 8)}</span>
            <button className="btn btn-ghost btn-sm" style={{ marginLeft: 4 }} onClick={() => { setRecord(null); setPage(1); }}>✕</button>
          </span>
        )}
      </div>

      {/* Log table */}
      {loading && rows.length === 0 ? (
        <div className="loading"><div className="spinner" /></div>
      ) : loadError && !tableMissing ? (
        <div className="empty-state">
          <Activity size={40} />
          <h3>Could not load activity logs</h3>
          <p>{loadError.message}</p>
          <button className="btn btn-secondary btn-sm" onClick={() => setRefreshKey(k => k + 1)}>Retry</button>
        </div>
      ) : tableMissing ? (
        <div className="empty-state">
          <Activity size={40} />
          <h3>Activity log is not set up</h3>
          <p>Run <b>activity_log_setup.sql</b> in the Supabase SQL Editor. The table itself:</p>
          <pre style={{ fontSize: 11, overflow: 'auto', background: 'var(--surface)', padding: 10, borderRadius: 6, maxWidth: 560, textAlign: 'left' }}>{SETUP_SQL}</pre>
        </div>
      ) : rows.length === 0 && !loading ? (
        <div className="empty-state">
          <Activity size={40} />
          <h3>{anyFilter ? 'No activity matches these filters' : 'No activity logs yet'}</h3>
          {!anyFilter && <p>Entries appear here as staff add, edit and delete records.</p>}
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--text3)', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>Showing {fmtN(firstRow)}–{fmtN(lastRow)}{total != null && total >= lastRow ? ` of about ${fmtN(total)}` : ''} activities</span>
            <span>Per page: <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }} style={{ fontSize: 12, padding: '2px 4px' }}>
              {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}</select></span>
            {loading && <span>Updating…</span>}
          </div>
          <div className="card" style={{ padding: 0, overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 340px)', opacity: loading ? 0.6 : 1 }}>
            <table className="activity-log-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 100 }}>
                <tr>
                  <th style={{ ...th, cursor: 'pointer' }} onClick={() => { setAsc(a => !a); setPage(1); }} title="Click to reverse the order">Time {asc ? '▲' : '▼'}</th>
                  <th style={th}>Staff</th>
                  <th style={th}>Action</th>
                  <th style={th}>Section</th>
                  <th style={th}>Description</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((l, i) => {
                  const cfg = actionCfg(l.action);
                  const open = expandedRow === l.id;
                  return (
                    <React.Fragment key={l.id}>
                      <tr tabIndex={0} aria-expanded={open}
                        onClick={() => toggleRow(l)}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleRow(l); } }}
                        style={{ borderTop: '1px solid var(--border)', background: open ? 'var(--accent-light)' : i % 2 === 0 ? 'transparent' : 'var(--bg3)', cursor: 'pointer' }}>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                          <div>{fmtDMY(dayKey(l.created_at))}</div>
                          <div style={{ fontSize: 11 }}>{timeOf(l.created_at)}</div>
                          <div style={{ fontSize: 10 }}>{timeAgo(l.created_at)}</div>
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--accent)', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {(l.staff_name || '?').split(' ').filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'}
                            </div>
                            <span style={{ fontSize: 13, fontWeight: 500 }}>{l.staff_name || '—'}</span>
                          </div>
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: cfg.bg, color: cfg.color, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                            {cfg.icon} {cfg.label}
                          </span>
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <span className="badge badge-blue" style={{ fontSize: 11 }}>{tableLabel(l.table_name)}</span>
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text2)' }}>{l.description || '—'}</td>
                      </tr>
                      {open && (
                        <tr style={{ background: 'var(--surface)' }}>
                          <td colSpan={5} style={{ padding: 0 }}>
                            <DetailPanel log={l} detail={details[l.id]} reveal={reveal} onHistory={openHistory} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {(page > 1 || hasNext) && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setPage(1)} disabled={page === 1}>« First</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Prev</button>
              <span style={{ padding: '6px 12px', fontSize: 13, color: 'var(--text3)' }}>Page {page}</span>
              <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => p + 1)} disabled={!hasNext}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
